import React, { useState, useEffect } from "react";
import { FaHistory, FaFile, FaTimes, FaChevronDown, FaChevronUp, FaCalendarAlt } from "react-icons/fa";
import { formatChatDate, formatChatTime } from "../utils/dateUtils";

const ChatHistory = ({ userId, isOpen, onClose }) => {
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [historyMessages, setHistoryMessages] = useState([]);
  const [historyDocuments, setHistoryDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [viewMode, setViewMode] = useState("messages"); // messages or documents
  const [expandedYear, setExpandedYear] = useState(null);

  useEffect(() => {
    if (isOpen && userId) {
      fetchAvailableYears();
    }
  }, [isOpen, userId]);

  const fetchAvailableYears = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/chat/available-years?userId=${userId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch available years");

      const data = await response.json();
      setAvailableYears(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchYearHistory = async (year) => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/chat/archived-history?userId=${userId}&year=${year}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) throw new Error(`No history found for ${year}`);

      const data = await response.json();
      setHistoryMessages(data.messages || []);
      setSelectedYear(year);
      setExpandedYear(year);
    } catch (error) {
      setError(error.message);
      setHistoryMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchYearDocuments = async (year) => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/chat/archived-documents?userId=${userId}&year=${year}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) throw new Error(`No documents found for ${year}`);

      const data = await response.json();
      setHistoryDocuments(data);
    } catch (error) {
      setError(error.message);
      setHistoryDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleYearClick = (year) => {
    if (expandedYear === year) {
      setExpandedYear(null);
      setSelectedYear(null);
      setHistoryMessages([]);
      setHistoryDocuments([]);
    } else {
      if (viewMode === "messages") {
        fetchYearHistory(year);
      } else {
        fetchYearDocuments(year);
      }
    }
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    setExpandedYear(null);
    setSelectedYear(null);
    setHistoryMessages([]);
    setHistoryDocuments([]);
  };

  const truncateFileName = (name) => {
    if (name && name.length > 30) {
      return `${name.substring(0, 15)}...${name.substring(name.length - 10)}`;
    }
    return name;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <FaHistory className="text-blue-600 text-xl" />
            <h3 className="text-xl font-semibold text-gray-800 dark:text-white">Chat History</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <FaTimes className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* View Mode Toggle */}
        <div className="flex gap-2 p-4 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => handleViewModeChange("messages")}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
              viewMode === "messages"
                ? "bg-blue-600 text-white"
                : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
            }`}
          >
            Messages
          </button>
          <button
            onClick={() => handleViewModeChange("documents")}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
              viewMode === "documents"
                ? "bg-blue-600 text-white"
                : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
            }`}
          >
            Documents
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            </div>
          )}

          {error && (
            <div className="p-4 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {!loading && availableYears.length === 0 && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <FaHistory className="mx-auto text-4xl mb-3 opacity-50" />
              <p>No archived chat history available</p>
            </div>
          )}

          {!loading && availableYears.length > 0 && (
            <div className="space-y-3">
              {availableYears.map((yearData) => (
                <div key={yearData.year} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <button
                    onClick={() => handleYearClick(yearData.year)}
                    className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <FaCalendarAlt className="text-blue-600" />
                      <div className="text-left">
                        <h4 className="font-semibold text-gray-800 dark:text-white">{yearData.year}</h4>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {yearData.messageCount} messages • Archived on {new Date(yearData.archivedAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    {expandedYear === yearData.year ? (
                      <FaChevronUp className="text-gray-600 dark:text-gray-400" />
                    ) : (
                      <FaChevronDown className="text-gray-600 dark:text-gray-400" />
                    )}
                  </button>

                  {expandedYear === yearData.year && (
                    <div className="p-4 bg-white dark:bg-gray-800 max-h-96 overflow-y-auto">
                      {viewMode === "messages" && historyMessages.length > 0 && (
                        <div className="space-y-3">
                          {historyMessages.map((msg, index) => (
                            <div
                              key={index}
                              className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"}`}
                            >
                              <div
                                className={`max-w-[80%] p-3 rounded-lg ${
                                  msg.sender === "user"
                                    ? "bg-blue-600 text-white rounded-br-md"
                                    : "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-white rounded-bl-md"
                                }`}
                              >
                                {(msg.fileUrl || (msg.files && msg.files.length > 0)) ? (
                                  <div className="flex flex-col gap-2">
                                    {msg.message && (
                                      <p className="text-sm whitespace-pre-wrap break-words">{msg.message}</p>
                                    )}
                                    {msg.files && msg.files.length > 0 ? (
                                      msg.files.map((file, fIdx) => (
                                        <div key={fIdx} className="flex items-center gap-2 p-2 bg-white/10 rounded-lg">
                                          <FaFile className="text-lg shrink-0" />
                                          <span className="text-sm truncate">{truncateFileName(file.originalName)}</span>
                                        </div>
                                      ))
                                    ) : (
                                      <div className="flex items-center gap-2 p-2 bg-white/10 rounded-lg">
                                        <FaFile className="text-lg shrink-0" />
                                        <span className="text-sm">{truncateFileName(msg.originalName)}</span>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <p className="text-sm whitespace-pre-wrap break-words">{msg.message}</p>
                                )}
                                <div className={`mt-1 text-xs ${msg.sender === "user" ? "text-white/60" : "text-gray-500"}`}>
                                  {formatChatTime(msg.timestamp)}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {viewMode === "documents" && historyDocuments.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {historyDocuments.map((doc, index) => (
                            <a
                              key={index}
                              href={`${process.env.REACT_APP_API_BASE_URL}${doc.fileUrl}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                            >
                              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                <FaFile className="text-blue-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-800 dark:text-white truncate">
                                  {truncateFileName(doc.originalName)}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {formatChatDate(doc.timestamp)}
                                </p>
                              </div>
                            </a>
                          ))}
                        </div>
                      )}

                      {viewMode === "documents" && historyDocuments.length === 0 && (
                        <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                          No documents found for {yearData.year}
                        </div>
                      )}

                      {viewMode === "messages" && historyMessages.length === 0 && (
                        <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                          No messages found for {yearData.year}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
            <strong>Note:</strong> Archived files cannot be removed. Chat transcripts are automatically archived at the end of each year.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChatHistory;
